// apps/client/src/state/slices/chat.ts (M0-T19, §8.7) — the chat log, the chat panel flag, the live bubbles and emote badges
import { tuning, type EmoteId } from '@whiskerton/content';
import type { ChatLine, ResidentId } from '@whiskerton/shared';
import { bubbleDuration } from '../../ui/chat/timing';
import type { SliceCreator } from '../types';
import { safetyReady } from './safety';

/** Transport backoff, not a feel number: while GET /api/blocks keeps failing, incoming chat retries it at most this often. */
const BLOCKS_RETRY_MS = 30_000;
let blocksRetry: { who: string | null; at: number } = { who: null, at: -Infinity };   // per account: a new sign-in loads at once

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends ChatSlice {} }

export interface ChatSlice {
  chatLines: ChatLine[]; chatOpen: boolean; chatBubbles: Record<ResidentId, { text: string; until: number }>;
  chatAdd(line: ChatLine, bubble: boolean, now: number): void; chatSetLines(lines: ChatLine[]): void; chatSetOpen(v: boolean): void; chatExpire(now: number): void;
  /** The emote a resident just played, shown as a badge above their tag (the body animation alone is hard to read at the default zoom). */
  emoteBadges: Record<ResidentId, { emote: EmoteId; until: number }>;
  emoteShow(r: ResidentId, emote: EmoteId, now: number): void;
}
export const slice: SliceCreator<ChatSlice> = (set, get) => ({
  chatLines: [], chatOpen: false, chatBubbles: {}, emoteBadges: {},
  chatAdd: (line, bubble, now) => {
    const s = get();
    // M2-T08: blocked players' lines are dropped on arrival (no log row, no bubble); the signed-in account's block list loads with its
    // first line (again after a sign-out or an account switch). Lines already in the history are filtered when the log renders
    // (visibleChatLines). A failed load is retried by a later line, at most every BLOCKS_RETRY_MS.
    const who = s.me?.user.id ?? null;
    if (!safetyReady(s) && (blocksRetry.who !== who || now >= blocksRetry.at)) {
      void s.safetyLoad().catch(() => { blocksRetry = { who, at: now + BLOCKS_RETRY_MS }; });
    }
    if (s.safetyIsBlockedResident(line.r)) return;
    if (get().chatLines.some((l) => l.id === line.id)) return;
    set({
      chatLines: [...get().chatLines, line].slice(-tuning.chat.logKeep),
      ...(bubble ? { chatBubbles: { ...get().chatBubbles, [line.r]: { text: line.text, until: now + bubbleDuration(line.text) } } } : {}),
    });
  },
  chatSetLines: (lines) => set({ chatLines: lines.slice(-tuning.chat.logKeep) }),
  chatSetOpen: (v) => set({ chatOpen: v }),
  chatExpire: (now) => {
    const b = get().chatBubbles, e = get().emoteBadges;
    const keep = Object.fromEntries(Object.entries(b).filter(([, v]) => v.until > now));
    if (Object.keys(keep).length !== Object.keys(b).length) set({ chatBubbles: keep as ChatSlice['chatBubbles'] });
    const keepE = Object.fromEntries(Object.entries(e).filter(([, v]) => v.until > now));
    if (Object.keys(keepE).length !== Object.keys(e).length) set({ emoteBadges: keepE as ChatSlice['emoteBadges'] });
  },
  emoteShow: (r, emote, now) => set({ emoteBadges: { ...get().emoteBadges, [r]: { emote, until: now + tuning.chat.emoteBadgeMs } } }),
});
